import { useEffect, useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Typography, useMediaQuery } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import { quranApi } from '../lib/quranApi'
import { getSurahInfo, getSurahSlug } from '../lib/surahs'
import { ARABIC_FONT_FAMILIES, arabicVerseFontSize } from '../lib/arabicFonts'
import type { ArabicFontSizeTier } from '../lib/arabicFonts'
import { usePrerenderReady } from '../hooks/usePrerenderReady'
import { useWakeLock } from '../hooks/useWakeLock'
import { isPrerendering } from '../lib/prerender'
import { usePreferences } from '../preferences/PreferencesContext'
import { SurahReaderChrome } from './SurahReaderChrome'
import {
  getCanonicalRedirect,
  buildPageTitle,
  buildPageDescription,
  buildSurahUrl,
  buildSurahIndexPath,
  buildSurahIndexUrl,
  SITE_ORIGIN,
  type SupportedLang,
} from '../lib/seo'
import type { Ayah, Surah, Translation } from '../types'

interface AyahData {
  ayahNumber: number
  arabic: string
  translation: string
}

const TRANSLATION_EDITION: Record<SupportedLang, string> = {
  en: 'en.sahih',
  fr: 'fr.hamidullah',
}

/**
 * Public, prerendered surah reader. The DOM shape below is what crawlers
 * see: a single <h1>, one <article id="ayah-N"> per ayah with Arabic
 * (lang=ar, dir=rtl) and translation (lang=<UI lang>), and a Bismillah
 * block (skipped for Surah 9, At-Tawbah).
 *
 * The Arabic font/size, translation visibility, and (post-hydration)
 * translation edition are all driven by the visitor's `prefs` — see
 * `usePreferences()` below — rather than the constants this page hard-
 * coded before the SEO/Reader unification. This is deliberate: React
 * mounts fresh over the prerendered HTML (no hydrateRoot), so the
 * prerender snapshot naturally captures `DEFAULT_PREFERENCES` (clean
 * localStorage, no visitor yet — the same default the interactive Reader
 * itself uses) while a real visitor's own prefs apply from their very
 * next render. DOM shape and text content stay exactly what they were;
 * only presentational output (font-family/size, whether the translation
 * paragraph renders) now varies with the active profile instead of being
 * frozen constants — see the reader-unification design doc's binding
 * invariants for why that is safe for indexing.
 *
 * Interactive chrome (audio player, settings rail, per-ayah action
 * buttons, and the "Continue in the full reader" CTA) lives in
 * `SurahReaderChrome`, mounted at the bottom of this tree. The chrome
 * wrapper short-circuits with `if (isPrerendering()) return null` so the
 * prerendered snapshot stays lean while real visitors get the full
 * interactive reader.
 */
export default function SurahReaderPage() {
  const { lang, surahNumber: surahParam, slug: slugParam } = useParams<{
    lang: string
    surahNumber: string
    slug: string
  }>()

  const { i18n } = useTranslation()

  const surahNumber = Number(surahParam)
  const surahNumberValid = Number.isInteger(surahNumber) && surahNumber >= 1 && surahNumber <= 114
  const langValid = lang === 'en' || lang === 'fr'

  // Drive the app language from the URL's `:lang` segment. The i18n detector
  // only reads localStorage/navigator (never the URL), so without this a
  // visitor landing on /fr/... got the English UI AND the document kept
  // `<html lang="en">` — which contradicts the page's own hreflang=fr /
  // og:locale=fr_FR and mis-targets French SERPs. Changing the language here
  // flows through i18n's `languageChanged` → `document.documentElement.lang`
  // sync (see i18n/config.ts), so the prerendered FR snapshots emit
  // `<html lang="fr">`. Runs before the data fetch so the lang is set well
  // before the prerender snapshot fires.
  useEffect(() => {
    if (langValid && i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
  }, [lang, langValid, i18n])

  const canonicalRedirect =
    surahNumberValid && langValid
      ? getCanonicalRedirect(lang as SupportedLang, surahNumber, slugParam)
      : null

  const [surahMeta, setSurahMeta] = useState<Surah | null>(null)
  const [ayahs, setAyahs] = useState<AyahData[] | null>(null)
  // Raw ayah list (full `Ayah` shape) is kept around for the interactive
  // chrome — `useAudioPlayer` and friends need fields beyond what the SEO
  // body renders (e.g. `surahNumber`, `juz`, `page`).
  const [rawAyahs, setRawAyahs] = useState<Ayah[]>([])

  // Reset before the fetch below starts: closes a latent race where, if a
  // surah-to-surah transition ever became an in-app `navigate()` instead
  // of today's full page reload, stale ayahs from the previous surah
  // would keep rendering under the new surah's already-updated <h1>/meta
  // until the new fetch resolved.
  useEffect(() => {
    setAyahs(null)
  }, [surahNumber])

  useEffect(() => {
    if (!surahNumberValid || !langValid || canonicalRedirect) return
    let cancelled = false
    const load = async () => {
      const result = await quranApi.getSurahWithTranslation(
        surahNumber,
        TRANSLATION_EDITION[lang as SupportedLang]
      )
      if (cancelled) return
      const trByNumber = new Map<number, string>(
        (result.translations as Translation[]).map((t) => [t.ayahNumber, t.text])
      )
      setSurahMeta(result.surah)
      setRawAyahs(result.ayahs as Ayah[])
      setAyahs(
        (result.ayahs as Ayah[]).map((a) => ({
          ayahNumber: a.ayahNumber,
          arabic: a.textUthmani ?? a.text,
          translation: trByNumber.get(a.ayahNumber) ?? '',
        }))
      )
    }
    load()
    return () => {
      cancelled = true
    }
  }, [surahNumber, surahNumberValid, lang, langValid, canonicalRedirect])

  const ayahsLoaded = ayahs !== null

  const { prefs } = usePreferences()

  // Post-hydration translation-edition swap. The fetch above always loads
  // the forced per-language default edition — that fetch (and its output)
  // stays identical regardless of prerendering, so the crawled snapshot's
  // translation text never depends on anything below. Once real ayahs are
  // on screen AND the visitor's preferred edition differs from that
  // forced default, re-fetch just that edition and swap the translation
  // text in place. Silent failure keeps the forced-default text already
  // on screen — there is no loading state to show because the page is
  // already fully readable.
  //
  // Guarded on `isPrerendering()` even though the design doc treats this
  // as needing no special-casing beyond what already gates the chrome:
  // `DEFAULT_PREFERENCES.edition` is a single global default ('en.sahih')
  // that is NOT language-aware, so on a clean French prerender profile
  // `prefs.edition` ('en.sahih') differs from the forced French default
  // ('fr.hamidullah') even though nothing was ever customized. Without
  // this guard, the prerendered French snapshot would silently refetch
  // and render the English translation in place of the French one,
  // breaking the byte-stability invariant this whole SEO surface exists
  // to protect.
  useEffect(() => {
    if (isPrerendering()) return
    if (!ayahsLoaded) return
    if (!surahNumberValid || !langValid || canonicalRedirect) return
    const forcedEdition = TRANSLATION_EDITION[lang as SupportedLang]
    if (prefs.edition === forcedEdition) return
    let cancelled = false
    quranApi
      .getSurahWithTranslation(surahNumber, prefs.edition)
      .then((result) => {
        if (cancelled) return
        const trByNumber = new Map<number, string>(
          (result.translations as Translation[]).map((t) => [t.ayahNumber, t.text])
        )
        setAyahs((prev) =>
          prev
            ? prev.map((a) => ({
                ...a,
                translation: trByNumber.get(a.ayahNumber) ?? a.translation,
              }))
            : prev
        )
      })
      .catch(() => {
        /* best-effort: keep the forced-default translation already on screen */
      })
    return () => {
      cancelled = true
    }
  }, [surahNumber, surahNumberValid, lang, langValid, canonicalRedirect, prefs.edition, ayahsLoaded])

  // Resolves the same three-tier formula VerseDisplay applies via an `sx`
  // breakpoint object (see arabicVerseFontSize's doc comment in
  // lib/arabicFonts.ts), but as a single value: this page renders the ayah
  // paragraph as plain HTML rather than an MUI component, so there is no
  // `sx` to attach a breakpoint object to. `600` / `900` are MUI's own
  // default `sm` / `md` breakpoints — the same thresholds VerseDisplay's
  // `sx` object resolves to today.
  const isSmUp = useMediaQuery('(min-width:600px)')
  const isMdUp = useMediaQuery('(min-width:900px)')
  const arabicSizeTier: ArabicFontSizeTier = isMdUp ? 'md' : isSmUp ? 'sm' : 'xs'
  const arabicFontSizePx = arabicVerseFontSize(prefs.fontSize, arabicSizeTier)

  usePrerenderReady(ayahs !== null)

  // This is a reading surface — hold the screen wake-lock for as long as
  // it's mounted, same as the interactive reader. Skip it during the SEO
  // prerender snapshot (Puppeteer): there's no real screen to keep awake,
  // and requesting a lock there would just be a wasted API call against a
  // headless, non-visible page.
  useWakeLock(!isPrerendering())

  if (!surahNumberValid || !langValid) {
    return <Navigate to="/" replace />
  }

  if (canonicalRedirect) {
    return <Navigate to={canonicalRedirect} replace />
  }

  // Static surah info from local SURAHS table; the API also returns surah
  // metadata, but having both lets the page render its header before the
  // network resolves (improves first-paint feel and gives the prerender
  // a head start).
  const info = surahMeta ?? getSurahInfo(surahNumber)
  if (!info) return <Navigate to="/" replace />

  const showBismillah = surahNumber !== 9

  const typedLang = lang as SupportedLang

  const prevUrl =
    surahNumber > 1
      ? `/${typedLang}/surah/${surahNumber - 1}/${getSurahSlug(surahNumber - 1)}`
      : null
  const nextUrl =
    surahNumber < 114
      ? `/${typedLang}/surah/${surahNumber + 1}/${getSurahSlug(surahNumber + 1)}`
      : null
  const altLang = typedLang === 'en' ? 'fr' : 'en'
  const altLangUrl = `/${altLang}/surah/${surahNumber}/${getSurahSlug(surahNumber)}`
  const hubPath = buildSurahIndexPath(typedLang)
  const hubLabel = typedLang === 'fr' ? 'Les 114 sourates' : 'All 114 surahs'

  const prevName = prevUrl ? getSurahInfo(surahNumber - 1)?.name : null
  const nextName = nextUrl ? getSurahInfo(surahNumber + 1)?.name : null

  // Normalize rather than blind-cast: the API/static table is trusted, but a
  // stray value would otherwise flow unchecked into the description string.
  const revelationType: 'Meccan' | 'Medinan' =
    info.revelationType === 'Medinan' ? 'Medinan' : 'Meccan'

  const title = buildPageTitle(typedLang, surahNumber)
  const description = buildPageDescription(
    typedLang,
    surahNumber,
    info.numberOfAyahs,
    revelationType
  )
  const canonical = buildSurahUrl(typedLang, surahNumber)
  const altEn = buildSurahUrl('en', surahNumber)
  const altFr = buildSurahUrl('fr', surahNumber)

  const creativeWork = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: info.name,
    alternateName: info.name,
    inLanguage: 'ar',
    position: surahNumber,
    numberOfPages: info.numberOfAyahs,
    translationOfWork: { '@type': 'CreativeWork', name: 'The Quran' },
    isPartOf: { '@type': 'CreativeWork', name: 'The Quran', alternateName: 'القرآن' },
  }
  // Three-level breadcrumb (Home → surah index → this surah). The middle item
  // is the real `/<lang>/surah` hub page; an earlier version pointed it at
  // `${SITE_ORIGIN}/${lang}/`, which is not a page (it 301s to `/`), and
  // pointing structured data at a non-page is a validation/quality risk.
  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: hubLabel,
        item: buildSurahIndexUrl(typedLang),
      },
      { '@type': 'ListItem', position: 3, name: info.name, item: canonical },
    ],
  }

  return (
    <Box component="main" sx={{ px: { xs: 2, sm: 0 }, maxWidth: 'lg', mx: 'auto' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={altEn} />
        <link rel="alternate" hrefLang="fr" href={altFr} />
        <link rel="alternate" hrefLang="x-default" href={altEn} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Qlearn" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {/* Square PWA icon used as the share image for now. A wide ~1.91:1
            branded image would render better in large-card previews; tracked
            as a follow-up (needs a design asset). */}
        <meta property="og:image" content={`${SITE_ORIGIN}/pwa-512.png`} />
        <meta property="og:locale" content={typedLang === 'fr' ? 'fr_FR' : 'en_US'} />
        <meta
          property="og:locale:alternate"
          content={typedLang === 'fr' ? 'en_US' : 'fr_FR'}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/pwa-512.png`} />
        <script type="application/ld+json">{JSON.stringify(creativeWork)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      </Helmet>

      {/* Breadcrumb. Structural, not decorative: it is the only link from a
          surah page back up to the crawl hub, so link equity flows in both
          directions instead of dead-ending in the 1→114 prev/next chain. */}
      <nav aria-label="Breadcrumb" style={{ marginBottom: 12, fontSize: '0.875rem' }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          Qlearn
        </a>
        <span aria-hidden="true" style={{ opacity: 0.5, margin: '0 8px' }}>
          /
        </span>
        <a href={hubPath} style={{ textDecoration: 'none' }}>
          {hubLabel}
        </a>
      </nav>

      <header>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          {`${info.name} · ${lang === 'fr' ? 'Sourate' : 'Surah'} ${surahNumber} ${
            lang === 'fr' ? 'du Coran' : 'of the Quran'
          }`}
        </Typography>
      </header>

      {showBismillah && (
        <Typography
          data-testid="bismillah"
          variant="h5"
          sx={{
            fontFamily: ARABIC_FONT_FAMILIES[prefs.arabicFont],
            direction: 'rtl',
            textAlign: 'center',
            my: 3,
          }}
        >
          بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
        </Typography>
      )}

      {ayahs &&
        ayahs.map((ayah) => (
          <article id={`ayah-${ayah.ayahNumber}`} key={ayah.ayahNumber} style={{ marginBottom: 24 }}>
            <span className="ayah-number" aria-label={`Ayah ${ayah.ayahNumber}`}>
              {ayah.ayahNumber}
            </span>
            <p
              lang="ar"
              dir="rtl"
              style={{
                fontFamily: ARABIC_FONT_FAMILIES[prefs.arabicFont],
                fontSize: `${arabicFontSizePx}px`,
                lineHeight: 2,
              }}
            >
              {ayah.arabic}
            </p>
            {prefs.showTranslation && <p lang={lang}>{ayah.translation}</p>}
          </article>
        ))}

      {/* Interactive chrome. Internally short-circuits with
          `if (isPrerendering()) return null`, so the prerendered HTML
          captures none of this — audio engine, settings rail, and
          per-ayah action buttons are visitor-only. */}
      <SurahReaderChrome surahNumber={surahNumber} ayahs={rawAyahs} />

      <footer style={{ marginTop: 48, paddingTop: 16, borderTop: '1px solid #e0e0e0' }}>
        <nav
          aria-label="Surah navigation"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          {prevUrl && prevName ? (
            <a rel="prev" href={prevUrl} style={{ textDecoration: 'none' }}>
              ← {prevName}
            </a>
          ) : (
            <span />
          )}
          <a hrefLang={altLang} href={altLangUrl} style={{ textDecoration: 'none' }}>
            {altLang === 'fr' ? 'Lire en français' : 'Read in English'}
          </a>
          {nextUrl && nextName ? (
            <a rel="next" href={nextUrl} style={{ textDecoration: 'none' }}>
              {nextName} →
            </a>
          ) : (
            <span />
          )}
        </nav>
        {/* No "open in interactive reader" link here (removed — was an
            ungated <a href="/reader/N">, a crawlable link into a
            robots.txt-Disallowed route, violating invariant 2). Every
            hydrated visitor already has that path via the
            `ContinueInReaderChrome` CTA (SurahReaderChrome.tsx), which is
            `position: fixed` and so stays on screen regardless of scroll
            position — a footer duplicate would only be redundant, gated
            or not. */}
      </footer>
    </Box>
  )
}
